import React, { useState, forwardRef } from 'react';
import styled from 'styled-components';

import useMutationUnfreezeFormaCard from 'hooks/useMutationUnfreezeFormaCard';
import useMutationFreezeFormaCard from 'hooks/useMutationFreezeFormaCard';

import ReplacePhysicalCard from './ReplacePhysicalCard';
import CardActionModal from './CardActionModal';

import Button from '@joinforma/ui/components/Button';
import { ButtonVariant, ButtonSize } from '@joinforma/ui/constants/properties';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';
import { ReactComponent as LockSvg } from '@joinforma/ui/assets/icons/lock.svg';
import { ReactComponent as UnlockSvg } from '@joinforma/ui/assets/icons/unlock.svg';
import { ReactComponent as ResetSvg } from '@joinforma/ui/assets/icons/back.svg';
import { ReactComponent as CreditCardSvg } from '@joinforma/ui/assets/icons/credit-card.svg';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import { CardReplacementReason } from 'constants/unit';

enum MODAL_STATES {
  ACTIVATE_CARD = 'activate',
  RESET_PIN = 'reset pin',
  REPLACE_VIRTUAL_CARD = 'replace virtual',
  REPLACE_PHYSICAL_CARD = 'replace physical',
  REPLACE_CARD_SHIPPING_ADDRESS = 'shipping address',
  // default state
  NONE = 'none',
}

interface CardActionButtonProps {
  highlighted?: boolean;
}

interface Props {
  cardType: string;
  cardId: string;
  is_frozen: boolean;
  shippingAddress: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    postalCode: string;
  };
  preferedAddress?: {
    street?: string;
    street_ext?: string;
    locality?: string;
    state?: string;
    country?: string;
    zip?: string;
  };

  refetchCards: () => void;
}

const RightColumn = styled.div`
  margin: 0 0 0 27px;

  @media (max-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    display: flex;
    flex-direction: column;
    margin: 25px 0 0;
  }
`;

const CardActionButton = styled(Button).attrs(
  (props: CardActionButtonProps) => ({
    variant: props.highlighted
      ? ButtonVariant.PRIMARY
      : ButtonVariant.SECONDARY,
    size: ButtonSize.SMALL,
  })
)<CardActionButtonProps>`
  width: 277px;
  height: 48px;
  margin: 12px 0;
  font-size: 15px;
  line-height: 15px;
`;

const replaceCardDescription = (reason: string) => {
  switch (reason) {
    case CardReplacementReason.LOST:
    case CardReplacementReason.STOLEN:
      return `Please confirm the mailing address you’d like your new card to be sent to. To change your address, go to Profile in your Settings page, and select edit. You’ll need to activate the card once you receive it.`;
    case CardReplacementReason.DAMAGED:
      return 'Please confirm the mailing address to receive your new card. Your card number will remain the same.';
  }
};

const CardActionColumn = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { cardId, is_frozen, shippingAddress, preferedAddress, refetchCards } =
    props;
  const [active_modal, setActiveModal] = useState(MODAL_STATES.NONE);
  const [replace_card_reason, setReplaceCardReason] = useState('');
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

  const continueToReplaceAddressForm = (reason: string) => {
    setReplaceCardReason(reason);
    setActiveModal(MODAL_STATES.REPLACE_CARD_SHIPPING_ADDRESS);
  };

  const { mutate: freezeFormaCard, isLoading: is_card_freezing } =
    useMutationFreezeFormaCard({
      onSuccess: () => {
        refetchCards();
        showSuccessSnackbar({
          text: 'Your card is now locked.',
        });
      },
      onError: () => {
        showErrorSnackbar({ text: 'An error occurred. Please try again.' });
      },
    });

  const { mutate: unfreezeFormaCard, isLoading: is_card_unfreezing } =
    useMutationUnfreezeFormaCard({
      onSuccess: () => {
        refetchCards();
        showSuccessSnackbar({
          text: 'Your card is now unlocked.',
        });
      },
      onError: () => {
        showErrorSnackbar({ text: 'An error occurred. Please try again.' });
      },
    });

  const handleCloseModal = () => setActiveModal(MODAL_STATES.NONE);

  const handleCardStatus = () => {
    is_frozen
      ? unfreezeFormaCard({ card_id: cardId })
      : freezeFormaCard({ card_id: cardId });
  };

  const is_card_loading = is_card_freezing || is_card_unfreezing;

  return (
    <>
      <RightColumn>
        <CardActionButton
          startIcon={ResetSvg}
          onClick={() => setActiveModal(MODAL_STATES.RESET_PIN)}
        >
          Reset PIN
        </CardActionButton>
        <CardActionButton
          startIcon={is_frozen ? UnlockSvg : LockSvg}
          loading={is_card_loading}
          onClick={handleCardStatus}
        >
          {is_frozen ? 'Unlock' : 'Lock'} card
        </CardActionButton>
        <CardActionButton
          startIcon={CreditCardSvg}
          onClick={() => setActiveModal(MODAL_STATES.REPLACE_PHYSICAL_CARD)}
        >
          Replace card
        </CardActionButton>
      </RightColumn>
      <CardActionModal
        is_open={active_modal === MODAL_STATES.REPLACE_PHYSICAL_CARD}
        title="Replace your physical debit card"
        description={
          'For which of the following reasons are you requesting to replace your physical debit card?'
        }
        onCancel={handleCloseModal}
      >
        <ReplacePhysicalCard
          replace_card_reason={replace_card_reason}
          onContinue={continueToReplaceAddressForm}
          onCancel={handleCloseModal}
          refetchCards={refetchCards}
        />
      </CardActionModal>
      <CardActionModal
        is_open={active_modal === MODAL_STATES.REPLACE_CARD_SHIPPING_ADDRESS}
        title="Replace your physical debit card"
        description={replaceCardDescription(replace_card_reason)}
        onBack={() => setActiveModal(MODAL_STATES.REPLACE_PHYSICAL_CARD)}
        is_replace_address_open={
          active_modal === MODAL_STATES.REPLACE_CARD_SHIPPING_ADDRESS
        }
        onCancel={handleCloseModal}
      >
        <ReplacePhysicalCard
          is_replace_card_address_open
          shipping_address={shippingAddress}
          preferred_address={preferedAddress}
          replace_card_reason={replace_card_reason}
          card_id={cardId}
          onCancel={handleCloseModal}
          refetchCards={refetchCards}
        />
      </CardActionModal>
      <CardActionModal
        is_open={active_modal === MODAL_STATES.RESET_PIN}
        title="Reset your PIN"
        card_action_description="to reset your PIN."
        onCancel={handleCloseModal}
      />
    </>
  );
});

export default CardActionColumn;
